import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faUpload, faCheckCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

// Local Document type for modal props
type Document = {
  id: string;
  name: string;
  type: 'file' | 'folder';
  path: string;
  children?: Document[];
};

interface SiteFileUploadModalProps {
  open: boolean;
  onClose: () => void;
  missingFile: Document | null;
  onUpload: (files: File[], markComplete: boolean) => void;
}

const SiteFileUploadModal: React.FC<SiteFileUploadModalProps> = ({ open, onClose, missingFile, onUpload }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [markComplete, setMarkComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!open || !missingFile) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = () => {
    onUpload(selectedFiles, markComplete);
    setSelectedFiles([]);
    setMarkComplete(false);
    onClose();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setMarkComplete(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-base-100 rounded-xl shadow-lg w-full max-w-md p-6 relative">
        <button className="absolute top-3 right-3 btn btn-ghost btn-sm" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning text-2xl" />
          <div>
            <div className="font-bold text-lg">Upload for Missing File</div>
            <div className="text-base-content/70 text-sm">{missingFile.name.replace(/_missing/i, '')}</div>
          </div>
        </div>
        <div className="mb-4 text-base-content/60 text-sm">
          This file is required for project closeout. You may upload one or more files to satisfy this requirement. If you have completed this requirement in another way, you can mark it as complete.
        </div>
        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="file-input file-input-bordered w-full"
            onChange={handleFileChange}
          />
          {selectedFiles.length > 0 && (
            <ul className="mt-2 text-xs text-base-content/70">
              {selectedFiles.map((file, idx) => (
                <li key={idx}>{file.name}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="form-control mb-4">
          <label className="label cursor-pointer gap-2">
            <input
              type="checkbox"
              className="checkbox checkbox-success"
              checked={markComplete}
              onChange={e => setMarkComplete(e.target.checked)}
            />
            <span className="label-text flex items-center gap-1">
              <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
              Mark this file as complete
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button className="btn btn-outline" onClick={handleClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 && !markComplete}
          >
            <FontAwesomeIcon icon={faUpload} className="mr-2" />
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteFileUploadModal; 